import React from "react";

// Renders a constrained subset of markdown into editorial prose.
// Supports: ## h2, ### h3, paragraphs, *italics*, **bold**, ordered/unordered lists,
// > blockquotes. Intentionally minimal—no external deps.
const inline = (text) => {
  const parts = [];
  let i = 0;
  const push = (node) => parts.push(node);
  while (i < text.length) {
    if (text.startsWith("**", i)) {
      const end = text.indexOf("**", i + 2);
      if (end !== -1) {
        push(<strong key={i}>{text.slice(i + 2, end)}</strong>);
        i = end + 2;
        continue;
      }
    }
    if (text[i] === "*" && text[i + 1] !== "*") {
      const end = text.indexOf("*", i + 1);
      if (end !== -1) {
        push(<em key={i}>{text.slice(i + 1, end)}</em>);
        i = end + 1;
        continue;
      }
    }
    // Plain text run
    let next = text.length;
    const starB = text.indexOf("**", i);
    const starI = text.indexOf("*", i);
    if (starB !== -1) next = Math.min(next, starB);
    if (starI !== -1 && starI !== starB) next = Math.min(next, starI);
    push(text.slice(i, next));
    i = next;
  }
  return parts;
};

const Markdown = ({ source = "" }) => {
  const lines = source.replace(/\r\n/g, "\n").split("\n");
  const blocks = [];
  let para = [];
  let list = null; // { ordered: bool, items: [] }
  let quote = [];

  const flushPara = () => {
    if (para.length) {
      blocks.push(<p key={`p-${blocks.length}`}>{inline(para.join(" "))}</p>);
      para = [];
    }
  };
  const flushList = () => {
    if (list) {
      const Tag = list.ordered ? "ol" : "ul";
      blocks.push(
        <Tag key={`l-${blocks.length}`}>
          {list.items.map((it, idx) => (
            <li key={idx}>{inline(it)}</li>
          ))}
        </Tag>
      );
      list = null;
    }
  };
  const flushQuote = () => {
    if (quote.length) {
      blocks.push(
        <blockquote key={`q-${blocks.length}`}>{inline(quote.join(" "))}</blockquote>
      );
      quote = [];
    }
  };
  const flushAll = () => { flushPara(); flushList(); flushQuote(); };

  for (const raw of lines) {
    const line = raw.trimEnd();
    if (!line.trim()) { flushAll(); continue; }
    if (line.startsWith("## ")) {
      flushAll();
      blocks.push(<h2 key={`h2-${blocks.length}`}>{inline(line.slice(3))}</h2>);
      continue;
    }
    if (line.startsWith("### ")) {
      flushAll();
      blocks.push(<h3 key={`h3-${blocks.length}`}>{inline(line.slice(4))}</h3>);
      continue;
    }
    if (line.startsWith("# ")) {
      flushAll();
      blocks.push(<h2 key={`h1-${blocks.length}`}>{inline(line.slice(2))}</h2>);
      continue;
    }
    if (line.startsWith("> ")) { flushPara(); flushList(); quote.push(line.slice(2)); continue; }
    const ol = line.match(/^(\d+)\.\s+(.*)$/);
    if (ol) {
      flushPara(); flushQuote();
      if (!list || !list.ordered) { flushList(); list = { ordered: true, items: [] }; }
      list.items.push(ol[2]);
      continue;
    }
    if (/^[-*]\s+/.test(line)) {
      flushPara(); flushQuote();
      if (!list || list.ordered) { flushList(); list = { ordered: false, items: [] }; }
      list.items.push(line.replace(/^[-*]\s+/, ""));
      continue;
    }
    flushList(); flushQuote();
    para.push(line);
  }
  flushAll();

  return <div className="prose-aviation">{blocks}</div>;
};

export default Markdown;
